import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * HashScrollManager - Handles scrolling to hash anchors BEFORE paint to avoid "hero flash".
 * Uses useLayoutEffect so the scroll happens synchronously before the browser paints.
 * 
 * Mount this component once at the app level or on pages that need hash scrolling.
 */
const HashScrollManager = () => {
  const location = useLocation();
  const didInitialScroll = useRef(false);
  const lastHash = useRef('');

  // Disable browser's automatic scroll restoration to prevent fighting
  useLayoutEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);

  // Handle hash scrolling BEFORE paint
  useLayoutEffect(() => {
    const hash = location.hash.replace('#', '');
    
    // Skip if no hash or same hash (prevents re-scrolling on re-renders)
    if (!hash) {
      // If navigating to home without hash, scroll to top
      if (location.pathname === '/' && !didInitialScroll.current) {
        didInitialScroll.current = true;
      }
      return;
    }

    // Skip if same hash (already handled)
    if (hash === lastHash.current) return;
    lastHash.current = hash;

    const element = document.getElementById(hash);
    if (!element) return;

    // Use 'auto' behavior for instant scroll (no animation) to prevent hero flash
    // The scroll-margin-top CSS handles the offset
    element.scrollIntoView({ behavior: 'auto', block: 'start' });
    didInitialScroll.current = true;
  }, [location.hash, location.pathname]);

  return null;
};

export default HashScrollManager;
